import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Drawer } from "@axisnode/ui";
import { useProject, useProjects } from "../api/hooks";
import { KNOWN_PROJECTS } from "../config";
import { listKnownProjectIds } from "../domain/projectIndex";

function ProjectRow({
  projectId,
  fallbackLabel,
  active,
  onNavigate,
}: {
  projectId: string;
  fallbackLabel?: string;
  active: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const q = useProject(projectId);
  const label = q.data?.name ?? fallbackLabel ?? `${projectId.slice(0, 8)}…`;
  const rawStatus = q.data?.status;
  const status = typeof rawStatus === "string" ? rawStatus : undefined;

  return (
    <Link
      to={`/projects/${projectId}`}
      data-testid={`sidebar-project-${projectId}`}
      onClick={onNavigate}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--ax-space-2)",
        padding: "var(--ax-space-2) var(--ax-space-3)",
        borderRadius: 6,
        textDecoration: "none",
        color: active ? "#ffffff" : "var(--ax-color-text)",
        background: active ? "var(--ax-color-accent)" : "transparent",
        fontSize: "var(--ax-type-sm)",
      }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {q.isLoading
          ? "…"
          : q.isError
            ? t("projectSidebar.loadError", { label })
            : label}
      </span>
      {status ? (
        <Badge variant={status === "Active" ? "success" : "default"}>
          {status === "Active" ? t("projectSidebar.active") : t("projectSidebar.archived")}
        </Badge>
      ) : null}
    </Link>
  );
}

/**
 * Persistent left-side project navigator (ADR-DEV-DDX-UI-001 extension).
 * Lists every live Project for this tenant (GET /projects, added because the
 * original KNOWN_PROJECTS+sessionStorage seed missed anything created outside
 * a browser tab — e.g. via a backend script), merged with KNOWN_PROJECTS
 * (canonical labels for the two oldest Projects) and the session navigation
 * index (ad hoc Projects from other tenants, opened directly by link). Each
 * row's name/status is always read live via useProject — this panel is
 * navigation, never a second source of business truth (ADR-DDX-UI-001 §13).
 */
export function ProjectSidebar() {
  const { t } = useTranslation();
  const { projectId: activeId } = useParams<{ projectId?: string }>();
  const [sessionIds] = useState(() => listKnownProjectIds());
  const [mobileOpen, setMobileOpen] = useState(false);
  const liveProjects = useProjects();

  const rows = useMemo(() => {
    const live = (liveProjects.data ?? []).map((p) => ({
      id: p.project_id,
      label: p.name,
    }));
    const seen = new Set(live.map((p) => p.id));

    const known = KNOWN_PROJECTS.filter((p) => !seen.has(p.id)).map((p) => {
      seen.add(p.id);
      return { id: p.id, label: p.label as string | undefined };
    });

    const extra = sessionIds
      .filter((id) => !seen.has(id))
      .map((id) => ({ id, label: undefined as string | undefined }));

    return [...live, ...known, ...extra];
  }, [liveProjects.data, sessionIds]);

  const closeMobile = () => setMobileOpen(false);

  // Shared between the persistent desktop <nav> and the mobile Drawer's
  // copy — same rows, same live data, only the surrounding chrome differs.
  // onNavigate is undefined on desktop (nothing to close) and closes the
  // drawer on mobile once a Project is picked.
  function renderRows(onNavigate?: () => void) {
    return (
      <>
        <Link
          to="/projects"
          onClick={onNavigate}
          style={{
            fontSize: "var(--ax-type-xs)",
            fontWeight: 600,
            color: "var(--ax-color-text-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            padding: "var(--ax-space-1) var(--ax-space-3)",
            textDecoration: "none",
          }}
        >
          {t("projectSidebar.navLabel")}
        </Link>
        {rows.length === 0 ? (
          <p
            style={{
              fontSize: "var(--ax-type-xs)",
              color: "var(--ax-color-text-muted)",
              padding: "0 var(--ax-space-3)",
            }}
          >
            {t("projectSidebar.noProjectsYet")}
          </p>
        ) : (
          rows.map((p) => (
            <ProjectRow
              key={p.id}
              projectId={p.id}
              {...(p.label ? { fallbackLabel: p.label } : {})}
              active={p.id === activeId}
              {...(onNavigate ? { onNavigate } : {})}
            />
          ))
        )}
        <Link
          to="/projects"
          data-testid="sidebar-new-project"
          onClick={onNavigate}
          style={{
            marginTop: "var(--ax-space-2)",
            padding: "var(--ax-space-2) var(--ax-space-3)",
            fontSize: "var(--ax-type-sm)",
            color: "var(--ax-color-accent)",
            textDecoration: "none",
          }}
        >
          {t("projectSidebar.newProject")}
        </Link>
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className="ax-sidebar-trigger"
        data-testid="sidebar-mobile-trigger"
        onClick={() => setMobileOpen(true)}
      >
        <span aria-hidden="true">☰</span> {t("projectSidebar.navLabel")}
      </button>

      <nav
        data-testid="project-sidebar"
        aria-label={t("projectSidebar.navLabel")}
        className="ax-project-sidebar"
      >
        {renderRows()}
      </nav>

      <Drawer open={mobileOpen} onClose={closeMobile} ariaLabel={t("projectSidebar.navLabel")}>
        <nav data-testid="project-sidebar-drawer" aria-label={t("projectSidebar.navLabel")}>
          {renderRows(closeMobile)}
        </nav>
      </Drawer>
    </>
  );
}
